/* eslint-disable react/prop-types */
import { FC, useCallback, useEffect, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import {
	Bold,
	Italic,
	Underline as UnderlineIcon,
	Link as LinkIcon,
	List,
	ListOrdered
} from "lucide-react";

interface Props {
	content: string;
	onChange: (html: string) => void;
	placeholder?: string;
}

const ToolbarButton: FC<{
	onClick: () => void;
	isActive?: boolean;
	title: string;
	children: React.ReactNode;
}> = ({ onClick, isActive = false, title, children }) => (
	<button
		type="button"
		onClick={onClick}
		title={title}
		className={[
			"p-2 rounded-md transition-colors",
			isActive
				? "bg-primary/10 text-primary"
				: "text-muted-foreground hover:bg-muted hover:text-foreground"
		].join(" ")}
	>
		{children}
	</button>
);

const RichTextEditor: FC<Props> = ({
	content,
	onChange,
	placeholder = "Write your announcement..."
}) => {
	const isInitialMount = useRef(true);

	const editor = useEditor({
		extensions: [
			StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
			Link.configure({
				openOnClick: false,
				HTMLAttributes: {
					class: "text-primary underline",
					rel: "noopener noreferrer"
				},
				validate: (href) => /^https?:\/\//i.test(href)
			}),
			Placeholder.configure({ placeholder }),
			Underline
		],
		content,
		onUpdate: ({ editor: ed }) => {
			onChange(ed.getHTML());
		},
		editorProps: {
			attributes: {
				class:
					"prose prose-sm max-w-none focus:outline-none min-h-[120px] px-4 py-3",
				"aria-label": placeholder,
				"aria-multiline": "true",
				role: "textbox"
			}
		}
	});

	useEffect(() => {
		if (!editor || editor.isDestroyed) return;
		if (isInitialMount.current) {
			isInitialMount.current = false;
			return;
		}
		if (content !== editor.getHTML()) {
			editor.commands.setContent(content, false);
		}
	}, [content, editor]);

	const setLink = useCallback(() => {
		if (!editor) return;
		const previousUrl = editor.getAttributes("link").href;
		const url = window.prompt("Enter URL:", previousUrl);
		if (url === null) return;
		if (url === "") {
			editor.chain().focus().extendMarkRange("link").unsetLink().run();
			return;
		}
		const trimmed = url.trim();
		if (!/^https?:\/\//i.test(trimmed)) {
			window.alert("Only http:// and https:// URLs are allowed.");
			return;
		}
		editor
			.chain()
			.focus()
			.extendMarkRange("link")
			.setLink({ href: trimmed })
			.run();
	}, [editor]);

	if (!editor) return null;

	return (
		<div className="rounded-md border border-input bg-white overflow-hidden">
			<div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-border bg-subtle">
				<ToolbarButton
					onClick={() => editor.chain().focus().toggleBold().run()}
					isActive={editor.isActive("bold")}
					title="Bold"
				>
					<Bold className="h-4 w-4" />
				</ToolbarButton>
				<ToolbarButton
					onClick={() => editor.chain().focus().toggleItalic().run()}
					isActive={editor.isActive("italic")}
					title="Italic"
				>
					<Italic className="h-4 w-4" />
				</ToolbarButton>
				<ToolbarButton
					onClick={() => editor.chain().focus().toggleUnderline().run()}
					isActive={editor.isActive("underline")}
					title="Underline"
				>
					<UnderlineIcon className="h-4 w-4" />
				</ToolbarButton>
				<div className="w-px h-5 bg-border mx-1" />
				<ToolbarButton
					onClick={setLink}
					isActive={editor.isActive("link")}
					title="Link"
				>
					<LinkIcon className="h-4 w-4" />
				</ToolbarButton>
				<div className="w-px h-5 bg-border mx-1" />
				<ToolbarButton
					onClick={() => editor.chain().focus().toggleBulletList().run()}
					isActive={editor.isActive("bulletList")}
					title="Bullet list"
				>
					<List className="h-4 w-4" />
				</ToolbarButton>
				<ToolbarButton
					onClick={() => editor.chain().focus().toggleOrderedList().run()}
					isActive={editor.isActive("orderedList")}
					title="Numbered list"
				>
					<ListOrdered className="h-4 w-4" />
				</ToolbarButton>
			</div>
			<EditorContent editor={editor} />
		</div>
	);
};

export default RichTextEditor;
